import {
  type AvailableContentStates,
  type BlogpostReference,
  type BlogpostReferenceOrContentId,
  type Content,
  type ContentType,
  type Excerpt,
  type PageReference,
  type PageReferenceOrContentId,
  type ResolvedBlogpostReference,
  type ResolvedBlogpostReferenceOrContentId,
  type ResolvedPageReference,
  type ResolvedPageReferenceOrContentId
} from '@databases/types/confluence';
import { uniq } from 'lodash-es';
import { createCachedSelector } from 're-reselect';

import { getContentKey } from '../../confluence/utils.ts';
import {
  type AsyncOperation,
  AsyncOperationStatus,
  validateStatus
} from '../async-operation.ts';

import { type ContentState } from './slice.ts';

type ContextState = {
  content: ContentState;
};

/**
 * Returns the content object with the specified content ID from the store. If the object does not exist, undefined is returned.
 * If there was an error loading the object, undefined is returned.
 * @throws If there was an error while loading the object, an error is thrown.
 */
export function selectContentByIdOptional<Type extends ContentType>(
  state: ContextState,
  { contentId }: { contentId: string | undefined }
): Content<Type> | undefined;
export function selectContentByIdOptional(
  state: ContextState,
  { contentId }: { contentId: string | undefined }
): Content | undefined {
  if (!contentId) {
    return undefined;
  }

  if (
    selectContentByIdStatus(state, { contentId })?.status === AsyncOperationStatus.ERROR
  ) {
    return undefined;
  }

  return state.content.content[contentId];
}

/**
 * Returns the page/blogpost with the specified space key and title from the store. If the page/blogpost does not exist or
 * there was an error while loading it, an error is thrown.
 * @throws If the page/blogpost does not exist or there was an error while loading it, an error is thrown.
 */
export function selectContentRefRequired(
  state: ContextState,
  { contentRef }: { contentRef: PageReference }
): ResolvedPageReference;
export function selectContentRefRequired(
  state: ContextState,
  { contentRef }: { contentRef: BlogpostReference }
): ResolvedBlogpostReference;
export function selectContentRefRequired(
  state: ContextState,
  { contentRef }: { contentRef: PageReference | BlogpostReference }
): ResolvedPageReference | ResolvedBlogpostReference;
export function selectContentRefRequired(
  state: ContextState,
  { contentRef }: { contentRef: PageReference | BlogpostReference }
): ResolvedPageReference | ResolvedBlogpostReference {
  validateStatus(selectContentRefStatus(state, { contentRef }));

  const result = selectContentRefOptional(state, { contentRef });
  if (!result) {
    throw new Error(
      `Could not find ${contentRef.type} ${contentRef.contentTitle}${
        contentRef.type === 'blogpost' ? ` (${contentRef.postingDay})` : ''
      } in space ${contentRef.spaceKey}.`
    );
  }
  return result;
}

/**
 * Returns the page/blogpost with the specified space key and title from the store. If the page/blogpost does not exist,
 * undefined is returned. If there was an error while loading the page/blogpost, undefined is returned.
 */
export function selectContentRefOptional(
  state: ContextState,
  { contentRef }: { contentRef: PageReferenceOrContentId | undefined }
): ResolvedPageReference | ResolvedBlogpostReference | undefined;
export function selectContentRefOptional(
  state: ContextState,
  { contentRef }: { contentRef: PageReferenceOrContentId | undefined }
): ResolvedBlogpostReference | ResolvedBlogpostReference | undefined;
export function selectContentRefOptional(
  state: ContextState,
  {
    contentRef
  }: { contentRef: PageReferenceOrContentId | BlogpostReferenceOrContentId | undefined }
): ResolvedPageReference | ResolvedBlogpostReference | undefined;
export function selectContentRefOptional(
  state: ContextState,
  {
    contentRef
  }: { contentRef: PageReferenceOrContentId | BlogpostReferenceOrContentId | undefined }
): ResolvedPageReference | ResolvedBlogpostReference | undefined {
  if (contentRef == null) {
    return undefined;
  } else if (
    selectContentRefStatus(state, { contentRef })?.status === AsyncOperationStatus.ERROR
  ) {
    return undefined;
  } else {
    if ('type' in contentRef) {
      const byKey = state.content.contentRefs[getContentKey(contentRef)];
      if (byKey) {
        return byKey;
      }
    }

    if (
      'contentId' in contentRef &&
      contentRef.contentId &&
      state.content.contentRefs[contentRef.contentId]
    ) {
      return state.content.contentRefs[contentRef.contentId];
    }
  }
}

const selectContentRefsById = createCachedSelector<
  ContextState,
  Record<string, ResolvedPageReferenceOrContentId | ResolvedBlogpostReferenceOrContentId>,
  Record<string, ResolvedPageReferenceOrContentId | ResolvedBlogpostReferenceOrContentId>
>(
  (state) => state.content.contentRefs,
  (contentRefs) => {
    return Object.fromEntries(
      Object.values(contentRefs).map((contentRef) => [contentRef.contentId, contentRef])
    );
  }
)(() => '');

/**
 * Returns the resolved content reference for the given content ID from the store. If no such content reference
 * is persisted, returns undefined.
 */
export function selectContentRefByIdOptional(
  state: ContextState,
  { contentId }: { contentId: string | undefined }
): ResolvedPageReferenceOrContentId | ResolvedBlogpostReferenceOrContentId | undefined {
  if (contentId == null) {
    return undefined;
  } else {
    return selectContentRefsById(state)[contentId];
  }
}

/**
 * Returns the load status of the loadContentById action for the given content ID.
 */
export function selectContentByIdStatus(
  state: ContextState,
  { contentId }: { contentId: string | undefined }
): AsyncOperation<void> | undefined {
  if (contentId == null) {
    return undefined;
  }

  return state.content.contentByIdStatus[contentId];
}

/**
 * Returns the load status of the loadContentRef action for the given content reference.
 */
export function selectContentRefStatus(
  state: ContextState,
  {
    contentRef
  }: { contentRef: PageReferenceOrContentId | BlogpostReferenceOrContentId | undefined }
): AsyncOperation<void> | undefined {
  if (contentRef == null) {
    return undefined;
  }

  if ('type' in contentRef) {
    const byKey = state.content.contentRefStatus[getContentKey(contentRef)];
    if (byKey) {
      return byKey;
    }
  }

  if ('contentId' in contentRef && contentRef.contentId) {
    return state.content.contentRefStatus[contentRef.contentId];
  }
}

/**
 * Selects the cover picture URL for the given content object.
 */
export function selectCoverPicture(
  state: ContextState,
  { contentId }: { contentId: string | undefined }
): string | undefined {
  if (contentId == null) {
    return undefined;
  }

  return state.content.coverPictureByContentId[contentId];
}

/**
 * Returns the load status of the loadCoverPicture action for the given content object.
 */
export function selectCoverPictureStatus(
  state: ContextState,
  { contentId }: { contentId: string | undefined }
): AsyncOperation<void> | undefined {
  if (contentId == null) {
    return undefined;
  }

  return state.content.coverPictureStatus[contentId];
}

/**
 * Selects the excerpt for the given content object.
 */
export function selectExcerpt(
  state: ContextState,
  { contentId }: { contentId: string | undefined }
): Excerpt | undefined {
  if (contentId == null) {
    return undefined;
  }

  return state.content.excerptByContentId[contentId];
}

/**
 * Returns the load status of the loadExcerpt action for the given content object.
 */
export function selectExcerptStatus(
  state: ContextState,
  { contentId }: { contentId: string | undefined }
): AsyncOperation<void> | undefined {
  if (contentId == null) {
    return undefined;
  }

  return state.content.excerptStatus[contentId];
}

export function selectAvailableContentStatesStatus(
  state: ContextState,
  { contentId }: { contentId: string }
): AsyncOperation<void> | undefined {
  return state.content.availableContentStatesStatus[contentId];
}

export function selectAvailableContentStates(
  state: ContextState,
  { contentId }: { contentId: string }
): AvailableContentStates | undefined {
  const status = selectAvailableContentStatesStatus(state, { contentId });
  if (status?.status === AsyncOperationStatus.ERROR) {
    return undefined;
  }
  return state.content.availableContentStates[contentId];
}

/**
 * Returns a list of content IDs of all content objects and content references currently in the store.
 */
export const selectAllContentIds = createCachedSelector<
  ContextState,
  Record<string, Content>,
  Record<string, ResolvedPageReferenceOrContentId | ResolvedBlogpostReferenceOrContentId>,
  string[]
>(
  (state) => state.content.content,
  (state) => state.content.contentRefs,
  (contentById, contentRefs) => {
    return uniq([
      ...Object.keys(contentById),
      ...Object.values(contentRefs).map((ref) => ref.contentId)
    ]);
  }
)(() => '');
